import './Pricing.css';
import { useState, useEffect } from 'react';
import { RedButton, GrayButton, OutlineButton, DiscordButton, getElapsedTime } from '../components/common';
import SupportEmail from '../components/SupportEmail';
import LegalNotice from '../components/LegalNotice';
import { pricingTiers } from '../data/pricing';
import { createSubscription } from '@genfun/core/api/auth';

export function PlanCard({ tier, timer, onBuy, loading }) {
    const isPremium = tier.featured;
    const isComingSoon = tier.comingSoon;
    // with a first-week price the card shows it against the regular weekly price
    const intro = isPremium && tier.intro;
    const currentPrice = intro ? intro.price : tier.price?.current;
    const originalPrice = intro ? tier.price.current : tier.price?.original;
    const salePercent = intro ? Math.round((1 - intro.price / tier.price.current) * 100) : tier.sale?.percent;

    return (
        <div className={`planCard ${isPremium ? 'planCardFeatured' : ''} ${isComingSoon ? 'planCardComingSoon' : ''}`}>
            <div className="planHeader">
                <div className="planTitleGroup">
                    <h2 className="planTitle">{tier.name}</h2>
                    {/* Sale text for Premium - plain white text */}
                    {isPremium && tier.sale && (
                        <p className="saleText">
                            {salePercent}% off {intro ? 'for the first week, ends in' : 'sale ends in'} {timer}
                        </p>
                    )}
                </div>

                {/* Price section */}
                {!isComingSoon && tier.price && (
                    <div className="priceSection">
                        {isPremium ? (
                            <div className="discountedPrice">
                                {/* Left pill - original price with strikethrough */}
                                <div className="priceOriginalPill">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/strike.svg`}
                                        alt=""
                                        className="strikethroughSvg"
                                    />
                                    <span className="priceOriginalText">${originalPrice}</span>
                                </div>
                                {/* Center pill - current price (on top) */}
                                <div className="priceCurrentPill">
                                    <span className="priceCurrentText">${currentPrice}</span>
                                    <span className="pricePerText">/w</span>
                                </div>
                                {/* Right pill - discount percentage */}
                                <div className="priceDiscountPill">
                                    <span className="priceDiscountText">−{salePercent}%</span>
                                </div>
                            </div>
                        ) : (
                            <div className="regularPrice">
                                <span className="priceRegular">${tier.price.current}</span>
                                <span className="pricePerText">/w</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Stats row for photo/video counts */}
            {!isComingSoon && tier.features && (
                <div className="statsRow">
                    <div className="statItem">
                        <span className="statNumber">{tier.features.photos}</span>
                        <span className="statLabel">photos</span>
                    </div>
                    <div className="statDivider"></div>
                    <div className="statItem">
                        <span className="statNumber">{tier.features.videos}</span>
                        <span className="statLabel">videos</span>
                    </div>
                </div>
            )}

            {/* No watermark feature */}
            {!isComingSoon && tier.features?.noWatermark && (
                <div className="featureItem">
                    <div className="featureIconWrapper">
                        <img src={`${process.env.PUBLIC_URL}/w-strikethrough.svg`} alt="" className="featureIcon" />
                    </div>
                    <span className="featureText">No watermark</span>
                </div>
            )}

            {/* Coming soon features - grid layout on tablet */}
            {isComingSoon && tier.upcomingFeatures && (
                <div className="upcomingFeatures">
                    {/* First row: 2 features side-by-side on tablet */}
                    <div className="upcomingFeaturesRow">
                        {tier.upcomingFeatures.slice(0, 2).map((feature, index) => (
                            <div key={index} className="featureItem featureItemHalf">
                                <div className="featureIconWrapper">
                                    <img src={`${process.env.PUBLIC_URL}/${feature.icon}`} alt="" className="featureIcon featureIconSmall" />
                                </div>
                                <span className="featureText">{feature.text}</span>
                            </div>
                        ))}
                    </div>
                    {/* Second row: remaining feature full-width */}
                    {tier.upcomingFeatures.slice(2).map((feature, index) => (
                        <div key={index + 2} className="featureItem">
                            <div className="featureIconWrapper">
                                <img src={`${process.env.PUBLIC_URL}/${feature.icon}`} alt="" className="featureIcon featureIconSmall" />
                            </div>
                            <span className="featureText">{feature.text}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Buy button */}
            {!isComingSoon && (
                <div className="planButton">
                    {isPremium ? (
                        <>
                            <RedButton onClick={onBuy} disabled={loading}>
                                {loading ? 'Loading...' : (intro ? 'Subscribe' : `Subscribe — $${currentPrice}/w`)}
                            </RedButton>
                            {intro && (
                                <p className="introTermsText">${tier.price.current}/w after the first week, cancel anytime</p>
                            )}
                        </>
                    ) : (
                        <GrayButton onClick={onBuy} disabled={loading}>
                            {loading ? 'Loading...' : `Subscribe — $${tier.price.current}/w`}
                        </GrayButton>
                    )}
                </div>
            )}
        </div>
    );
}

function Footer() {
    return (
        <footer className="pricingFooter">
            <SupportEmail />
            <div className="footerButtons">
                <div className="policyButtons">
                    <OutlineButton onClick={() => window.open('/terms', '_blank')}>
                        Terms of use
                    </OutlineButton>
                    <OutlineButton onClick={() => window.open('/privacy', '_blank')}>
                        Privacy policy
                    </OutlineButton>
                    <OutlineButton onClick={() => window.open('/take_it_down_report', '_blank')}>
                        Report content
                    </OutlineButton>
                </div>
                {/*<div className="discordButtonWrapper">*/}
                {/*    <DiscordButton onClick={() => window.open('https://discord.gg/your-server', '_blank')} />*/}
                {/*</div>*/}
            </div>
            <LegalNotice />
        </footer>
    );
}

export default function Pricing({ token }) {
    const [timer, setTimer] = useState("29:59");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimer(getElapsedTime(30 * 60 * 1000)); // 30 minutes
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleBuy = async (tier) => {
        if (!token || loading) return;
        setLoading(true);
        try {
            const tierNum = tier.id === 'premium' ? 2 : 1;
            const result = await createSubscription(token, tierNum);
            window.location.href = result.url;
        } catch (err) {
            console.error('Subscription error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pricingPage">
            <div className="pricingContent">
                <div className="pricingCards">
                    {pricingTiers.filter((tier) => tier.id !== 'starter').map((tier) => (
                        <PlanCard
                            key={tier.id}
                            tier={tier}
                            timer={timer}
                            onBuy={() => handleBuy(tier)}
                            loading={loading}
                        />
                    ))}
                </div>
            </div>
            <Footer />
        </div>
    );
}
